import { useMemo, useState } from 'react'
import { BarChart3, CalendarDays, Landmark, Trophy, Wallet } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { Card } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart'
import { cn } from '@/lib/cn'
import { useUIStore } from '@/stores/useUIStore'
import { getCompanyColor, hexToRgba } from '@/features/shifts/utils/companyColor'
import { useWorkspaceCompanyFilter } from '@/features/companies/components/WorkspaceCompanyFilter'
import { useExpenseAnalytics } from '@/features/expenses/hooks/useExpenses'
import type {
  AnalyticsPeriodMode,
  ExpenseAnalyticsQuery,
} from '@/features/expenses/types/expense.types'
import {
  categoryColorMap,
  expenseCategoryLabel,
  formatExpenseAmount,
  deriveDateRange,
  toDateString,
  CHART_NEUTRAL_COLOR,
  CHART_SERIES_COLORS,
} from './expenseUtils'
import { useOrgCurrency } from '@/hooks/useOrgCurrency'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { ExpenseScopeFilter } from './ExpenseScopeFilter'
import { ExpensePeriodPicker } from './ExpensePeriodPicker'
import { ORGANIZATION_LABEL, scopedCompanyIds, useExpenseScope } from './expenseScope'
import {
  averagePerBucket,
  fillTrend,
  formatShare,
  rankWithShare,
  stackTrend,
  type RankedRow,
  type StackSeries,
} from './analyticsSeries'

/** Key of the organization's own row in the company list. */
const ORGANIZATION_KEY = '__organization__'
/**
 * The organization's colour: a strong slate, apart from every category and
 * company hue, and darker than the warm grey of "Other".
 */
const ORGANIZATION_COLOR = { light: '#334155', dark: '#cbd5e1' } as const

type ColoredRow = RankedRow & { color: string }

/** Compact axis ticks: 240000 becomes "240k", so the y-axis stops eating width. */
function formatCompact(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (Math.abs(value) >= 1_000) return `${Math.round(value / 1_000)}k`
  return String(value)
}

export function ExpenseAnalyticsPage(): React.ReactElement {
  const filter = useWorkspaceCompanyFilter()
  const scope = useExpenseScope()
  const [mode, setMode] = useState<AnalyticsPeriodMode>('month')
  const today = toDateString(new Date())
  const [anchor, setAnchor] = useState(today)
  const [rangeFrom, setRangeFrom] = useState(deriveDateRange('month', today).dateFrom)
  const [rangeTo, setRangeTo] = useState(today)
  const themeMode = useUIStore((s) => s.theme)

  const { dateFrom, dateTo } = useMemo(() => {
    if (mode === 'range') return { dateFrom: rangeFrom, dateTo: rangeTo }
    return deriveDateRange(mode, anchor)
  }, [mode, anchor, rangeFrom, rangeTo])

  const query = useMemo<ExpenseAnalyticsQuery>(
    () => ({
      scope: scope.scope,
      companyIds: scopedCompanyIds(scope.scope, filter.companyIds),
      mode,
      dateFrom,
      dateTo,
    }),
    [filter.companyIds, scope.scope, mode, dateFrom, dateTo],
  )

  const { currency: orgCurrency } = useOrgCurrency()
  const { data, isLoading, error, refetch } = useExpenseAnalytics(query)
  const currency = data?.currency || orgCurrency

  const formatAmount = (value: number): string => formatExpenseAmount(value, currency)

  const total = data?.total ?? 0
  const bucket = data?.trendBucket ?? (mode === 'year' ? 'month' : 'day')

  const trend = useMemo(
    () => fillTrend(data?.trend ?? [], bucket, dateFrom, dateTo),
    [data?.trend, bucket, dateFrom, dateTo],
  )
  const fullLabelByPeriod = useMemo(
    () => new Map(trend.map((t) => [t.period, t.fullLabel])),
    [trend],
  )
  // Whole units: cents on an average are noise.
  const average = Math.round(averagePerBucket(total, bucket, dateFrom, dateTo))

  // One colour per category, shared by the chart, its legend and the list.
  const categories = useMemo<ColoredRow[]>(() => {
    const ranked = rankWithShare(
      (data?.byCategory ?? []).map((c) => ({
        key: c.category,
        name: expenseCategoryLabel(c.category),
        value: c.total,
      })),
      total,
    )
    // Colours go to the categories shown; those folded into "Other" take none.
    const categoryColors = categoryColorMap(
      ranked.filter((row) => !row.folded).map((row) => row.key),
      themeMode,
    )
    return ranked.map((row) => ({
      ...row,
      color: row.folded
        ? CHART_NEUTRAL_COLOR[themeMode]
        : (categoryColors.get(row.key) ?? CHART_NEUTRAL_COLOR[themeMode]),
    }))
  }, [data?.byCategory, total, themeMode])

  const companyColorById = useMemo(
    () =>
      new Map(
        filter.companies.map((c) => [
          c._id,
          getCompanyColor(c._id, (c as unknown as { color?: string }).color).dot,
        ]),
      ),
    [filter.companies],
  )
  const organizationColor = ORGANIZATION_COLOR[themeMode]
  const companies = useMemo<ColoredRow[]>(
    () =>
      rankWithShare(
        (data?.byCompany ?? []).map((c) => ({
          key: c.companyId ?? ORGANIZATION_KEY,
          name: c.companyId
            ? (filter.companyNameById.get(c.companyId) ?? 'Unknown company')
            : ORGANIZATION_LABEL,
          value: c.total,
        })),
        total,
      ).map((row) => ({
        ...row,
        color:
          row.key === ORGANIZATION_KEY
            ? organizationColor
            : row.folded
              ? CHART_NEUTRAL_COLOR[themeMode]
              : (companyColorById.get(row.key) ?? CHART_NEUTRAL_COLOR[themeMode]),
      })),
    [
      data?.byCompany,
      filter.companyNameById,
      total,
      companyColorById,
      organizationColor,
      themeMode,
    ],
  )
  const organizationTotal = data?.byCompany?.find((c) => c.companyId === null)?.total ?? 0
  // The split between organization and companies only means something when
  // both are in view; within one kind it is all or nothing.
  const showSplit = scope.scope === 'all'

  // Each column is stacked by category, in the same colours and order as the
  // category list; categories folded there into "Other" fold here too.
  const stackSeries = useMemo<StackSeries[]>(
    () =>
      categories.map((row, index) => ({
        key: row.folded ? 'other' : `s${index}`,
        name: row.name,
        color: row.color,
      })),
    [categories],
  )
  const stackedTrend = useMemo(() => {
    const keyByCategory = new Map(
      categories.filter((row) => !row.folded).map((row, index) => [row.key, `s${index}`]),
    )
    return stackTrend(trend, data?.trendByCategory ?? [], keyByCategory)
  }, [trend, categories, data?.trendByCategory])
  const trendConfig = useMemo<ChartConfig>(
    () =>
      Object.fromEntries(
        stackSeries.map((series) => [series.key, { label: series.name, color: series.color }]),
      ),
    [stackSeries],
  )

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Expenses', to: '/expenses' }, { label: 'Expense Analytics' }]}
      icon={BarChart3}
      title="Expense Analytics"
      description="Visualize spending across companies, categories and time."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={<ExpenseScopeFilter scope={scope} companies={filter} />}
          actions={
            <ExpensePeriodPicker
              mode={mode}
              onModeChange={setMode}
              anchor={anchor}
              onAnchorChange={setAnchor}
              rangeFrom={rangeFrom}
              rangeTo={rangeTo}
              onRangeChange={({ from, to }) => {
                setRangeFrom(from)
                setRangeTo(to)
              }}
              today={today}
            />
          }
        />

        {isLoading ? (
          <SectionLoader variant="cards" rows={3} label="Loading expense analytics" />
        ) : error ? (
          <Card>
            <QueryErrorState
              error={error}
              title="Couldn't load expense analytics"
              onRetry={refetch}
              className="border-0 bg-transparent dark:border-0 dark:bg-transparent"
            />
          </Card>
        ) : (
          <>
            <div
              className={cn(
                'grid grid-cols-2 gap-3 sm:gap-4',
                showSplit ? 'xl:grid-cols-4' : 'xl:grid-cols-3',
              )}
            >
              <StatTile
                icon={Wallet}
                tint={CHART_SERIES_COLORS[0][themeMode]}
                label="Total spent"
                value={formatAmount(total)}
              />
              <StatTile
                icon={CalendarDays}
                tint={CHART_SERIES_COLORS[2][themeMode]}
                label={bucket === 'month' ? 'Average per month' : 'Average per day'}
                value={formatAmount(average)}
                detail={bucket === 'month' ? 'Over the months so far' : 'Over the days so far'}
              />
              {showSplit && (
                <StatTile
                  icon={Landmark}
                  tint={organizationColor}
                  label="Organization expenses"
                  value={formatAmount(organizationTotal)}
                  detail={
                    total > 0
                      ? `${formatShare(organizationTotal / total)} of the total`
                      : 'Nothing yet'
                  }
                />
              )}
              <StatTile
                className={showSplit ? undefined : 'col-span-2 xl:col-span-1'}
                icon={Trophy}
                tint={categories[0]?.color ?? CHART_SERIES_COLORS[3][themeMode]}
                label="Top category"
                value={categories[0] ? categories[0].name : '—'}
                detail={
                  categories[0]
                    ? `${formatAmount(categories[0].value)} · ${formatShare(categories[0].share)}`
                    : 'Nothing yet'
                }
              />
            </div>

            <Card
              header={
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold">Spending over time</h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {bucket === 'month' ? 'By month' : 'By day'}
                  </span>
                </div>
              }
            >
              {total === 0 ? (
                <EmptyState />
              ) : (
                <>
                  <ChartContainer
                    config={trendConfig}
                    className="aspect-auto h-[280px] w-full [--chart-surface:#fff] dark:[--chart-surface:#1f2937]"
                  >
                    <BarChart data={stackedTrend} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                      <CartesianGrid
                        vertical={false}
                        className="stroke-gray-100 dark:stroke-gray-700/60"
                      />
                      <XAxis
                        dataKey="period"
                        tickFormatter={(period: string) =>
                          trend.find((t) => t.period === period)?.label ?? period
                        }
                        interval="preserveStartEnd"
                        minTickGap={20}
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        width={48}
                        tick={{ fontSize: 12 }}
                        tickFormatter={formatCompact}
                      />
                      <ChartTooltip
                        cursor={{ fill: 'currentColor', fillOpacity: 0.05 }}
                        content={
                          <StackTooltip
                            series={stackSeries}
                            fullLabelByPeriod={fullLabelByPeriod}
                            formatAmount={formatAmount}
                          />
                        }
                      />
                      {stackSeries.map((series) => (
                        <Bar
                          key={series.key}
                          dataKey={series.key}
                          name={series.name}
                          stackId="spent"
                          fill={`var(--color-${series.key})`}
                          // A hairline of the card colour keeps stacked segments apart.
                          stroke="var(--chart-surface)"
                          strokeWidth={1}
                          maxBarSize={24}
                        />
                      ))}
                    </BarChart>
                  </ChartContainer>
                  <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-600 dark:text-gray-300">
                    {stackSeries.map((series) => (
                      <li key={series.key} className="flex items-center gap-1.5">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                          style={{ backgroundColor: series.color }}
                          aria-hidden
                        />
                        {series.name}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Card>

            <div
              className={cn(
                'grid grid-cols-1 gap-4',
                scope.scope !== 'organization' && 'lg:grid-cols-2',
              )}
            >
              <Card header={<h3 className="text-sm font-semibold">By category</h3>}>
                {categories.length === 0 ? (
                  <EmptyState />
                ) : (
                  <RankedBars rows={categories} formatAmount={formatAmount} />
                )}
              </Card>
              {scope.scope !== 'organization' && (
                <Card
                  header={
                    <h3 className="text-sm font-semibold">
                      {showSplit ? 'Organization & companies' : 'By company'}
                    </h3>
                  }
                >
                  {companies.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <RankedBars rows={companies} formatAmount={formatAmount} />
                  )}
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </PageShell>
  )
}

function StatTile({
  className,
  icon: Icon,
  tint,
  label,
  value,
  detail,
}: {
  className?: string
  icon: React.ComponentType<{ className?: string }>
  /** The tile's accent: the icon and a soft wash behind it. */
  tint: string
  label: string
  value: string
  detail?: string
}): React.ReactElement {
  return (
    <Card className={cn('relative overflow-hidden', className)}>
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-1"
        style={{ backgroundColor: tint }}
      />
      <div className="flex items-start gap-3">
        <div
          className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg sm:flex"
          style={{ backgroundColor: hexToRgba(tint, 0.14), color: tint }}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</p>
          <p className="truncate text-lg font-semibold text-gray-900 sm:text-xl dark:text-gray-100">
            {value}
          </p>
          {detail && (
            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{detail}</p>
          )}
        </div>
      </div>
    </Card>
  )
}

/**
 * Ranked horizontal bars as plain rows: name, amount and share stay readable
 * at any width and long names wrap instead of being clipped by a chart axis.
 * Bars are scaled to the largest row, so the leader always spans the track.
 * Each row wears its entity's colour as a swatch and a bar; the text stays ink.
 */
function RankedBars({
  rows,
  formatAmount,
}: {
  rows: ColoredRow[]
  formatAmount: (value: number) => string
}): React.ReactElement {
  const max = Math.max(...rows.map((r) => r.value), 1)
  return (
    <ul className="space-y-3.5">
      {rows.map((row) => (
        <li
          key={row.key}
          title={`${row.name}: ${formatAmount(row.value)} (${formatShare(row.share)})`}
        >
          <div className="mb-1.5 flex items-baseline justify-between gap-3 text-sm">
            <span className="flex min-w-0 items-center gap-2 text-gray-900 dark:text-gray-100">
              {row.key === ORGANIZATION_KEY ? (
                <Landmark
                  className="h-3.5 w-3.5 shrink-0"
                  style={{ color: row.color }}
                  aria-hidden
                />
              ) : (
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                  style={{ backgroundColor: row.color }}
                  aria-hidden
                />
              )}
              <span className="truncate">{row.name}</span>
            </span>
            <span className="shrink-0 tabular-nums">
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {formatAmount(row.value)}
              </span>
              <span className="ml-2 inline-block w-9 text-right text-xs text-gray-500 dark:text-gray-400">
                {formatShare(row.share)}
              </span>
            </span>
          </div>
          <div
            className="h-2 overflow-hidden rounded-full"
            style={{ backgroundColor: hexToRgba(row.color, 0.14) }}
          >
            <div
              className="h-full rounded-full"
              style={{
                backgroundColor: row.color,
                width: `${Math.max((row.value / max) * 100, row.value > 0 ? 1.5 : 0)}%`,
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

/** Tooltip for the stacked columns: the day's total, then what it went on. */
function StackTooltip({
  active,
  payload,
  label,
  series,
  fullLabelByPeriod,
  formatAmount,
}: {
  active?: boolean
  payload?: { dataKey?: string | number; value?: number | string }[]
  label?: string | number
  series: StackSeries[]
  fullLabelByPeriod: Map<string, string>
  formatAmount: (value: number) => string
}): React.ReactElement | null {
  if (!active || !payload?.length) return null
  const byKey = new Map(series.map((s) => [s.key, s]))
  const rows = payload
    .map((p) => ({ series: byKey.get(String(p.dataKey)), value: Number(p.value) || 0 }))
    .filter((r): r is { series: StackSeries; value: number } => !!r.series && r.value > 0)
    .sort((a, b) => b.value - a.value)
  const sum = rows.reduce((acc, r) => acc + r.value, 0)
  return (
    <div className="min-w-[11rem] rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-gray-700 dark:bg-gray-900">
      <p className="font-medium text-gray-900 dark:text-gray-100">
        {fullLabelByPeriod.get(String(label)) ?? String(label)}
      </p>
      <p className="mb-1.5 font-semibold tabular-nums text-gray-900 dark:text-gray-100">
        {formatAmount(sum)}
      </p>
      {rows.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">Nothing spent</p>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r.series.key} className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                style={{ backgroundColor: r.series.color }}
                aria-hidden
              />
              <span className="flex-1 truncate text-gray-600 dark:text-gray-400">
                {r.series.name}
              </span>
              <span className="tabular-nums text-gray-900 dark:text-gray-100">
                {formatAmount(r.value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function EmptyState(): React.ReactElement {
  return (
    <div className="flex h-[200px] items-center justify-center text-sm text-gray-500 dark:text-gray-400">
      No expenses in this period.
    </div>
  )
}

ExpenseAnalyticsPage.displayName = 'ExpenseAnalyticsPage'
